import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { ArrowRight, GraduationCap, Briefcase, Rocket, Crown, CheckCircle2 } from "lucide-react";

const PROGRAMS = [
  {
    slug: "bachelor",
    num: "01",
    icon: GraduationCap,
    level: "BAC+3 — 3 ans",
    title: "Bachelor en Management",
    short: "Maîtrisez les fondamentaux du business avec un stage final. L'essentiel pour démarrer votre carrière.",
    modules: ["Management des organisations", "Marketing digital", "Gestion financière", "Développement commercial", "Communication & négociation"],
    outcomes: ["Chef de projet", "Consultant junior", "Entrepreneur"],
    note: "70% pratique · Stages en entreprise · Projets réels",
  },
  {
    slug: "master-strategy",
    num: "02",
    icon: Briefcase,
    level: "BAC+5 — 2 ans",
    title: "Master Management & Stratégie",
    short: "Devenez un leader stratégique, capable de piloter la transformation des organisations.",
    modules: ["Stratégie d'entreprise", "Leadership", "Innovation disruptive", "Business international", "Mémoire professionnel"],
    outcomes: ["Directeur stratégie", "Manager de transition", "Consultant senior"],
    note: "Mémoire professionnel ou création d'entreprise",
  },
  {
    slug: "master-entrepreneuriat",
    num: "03",
    icon: Rocket,
    level: "BAC+5 — 2 ans",
    title: "Master Entrepreneuriat",
    short: "Lancez votre startup avec l'accompagnement de mentors expérimentés.",
    modules: ["Business plan", "Levée de fonds", "Écosystème startup", "Pitch training", "Growth hacking"],
    outcomes: ["Fondateur de startup", "Intrapreneur", "Responsable innovation"],
    note: "Passez de l'idée à l'action avec un écosystème conçu pour entreprendre.",
  },
  {
    slug: "executive-mba",
    num: "04",
    icon: Crown,
    level: "Executive — 11 mois",
    title: "Executive MBA",
    short: "Pour les cadres et dirigeants qui veulent accélérer leur carrière.",
    modules: ["Leadership exécutif", "Transformation digitale", "Gouvernance d'entreprise", "Finance pour dirigeants", "Learning expéditions"],
    outcomes: ["Directeur général", "Cadre dirigeant", "Board member"],
    note: "Format week-ends + learning expéditions internationales",
  },
];

const ProgramCard = ({ p, index, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = p.icon;

  return (
    <motion.button
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -8 }}
      onClick={onOpen}
      data-testid={`program-card-${p.slug}`}
      className="relative bg-white border border-navy-100 overflow-hidden isolate text-left w-full"
      style={{
        boxShadow: hovered
          ? "0 30px 70px -20px rgba(0,35,98,0.30), 0 0 0 1px rgba(237,41,57,0.10)"
          : "0 8px 28px -16px rgba(0,35,98,0.12)",
      }}
    >
      {/* Gradient hover */}
      <div className={`absolute inset-0 bg-gradient-to-br from-navy-500 via-navy-700 to-[#000e27] origin-bottom transition-transform duration-[700ms] ease-[cubic-bezier(0.65,0,0.35,1)] -z-10 ${hovered ? "scale-y-100" : "scale-y-0"}`} />

      {/* Dot pattern */}
      <svg className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-700 ${hovered ? "opacity-[0.08]" : "opacity-0"}`} preserveAspectRatio="none">
        <defs>
          <pattern id={`prog-pat-${p.slug}`} width="16" height="16" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="white" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#prog-pat-${p.slug})`} />
      </svg>

      {/* Glow orb */}
      <div className={`absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl transition-opacity duration-700 ${hovered ? "opacity-100 bg-red-500/30" : "opacity-0"}`} />

      {/* Big number */}
      <span className={`absolute -top-2 right-4 font-display text-[160px] leading-none select-none pointer-events-none transition-all duration-500 ${hovered ? "text-white/[0.08] scale-110" : "text-navy-700/[0.06] scale-100"}`} aria-hidden>
        {p.num}
      </span>

      <div className="relative p-8 md:p-10 min-h-[320px] flex flex-col">
        {/* Icon */}
        <motion.div
          animate={{ rotate: hovered ? -8 : -4, scale: hovered ? 1.05 : 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-14 h-14 mb-6"
        >
          <div className={`absolute inset-0 transition-colors duration-500 rounded-lg ${hovered ? "bg-red-500" : "bg-navy-50"}`} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className={`h-6 w-6 transition-colors duration-500 ${hovered ? "text-white" : "text-navy-500"}`} strokeWidth={1.5} />
          </div>
        </motion.div>

        {/* Level */}
        <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 transition-colors duration-500 ${hovered ? "text-red-400" : "text-red-500"}`}>
          {p.level}
        </div>

        {/* Title */}
        <h3 className={`font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors duration-500 ${hovered ? "text-white" : "text-navy-700"}`}>
          {p.title}
        </h3>

        {/* Desc */}
        <p className={`text-[14px] leading-relaxed flex-1 transition-colors duration-500 ${hovered ? "text-white/80" : "text-[#555]"}`}>
          {p.short}
        </p>

        {/* CTA */}
        <div className={`mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-500 ${hovered ? "text-white" : "text-navy-700"}`}>
          Voir le programme
          <ArrowRight className={`h-3.5 w-3.5 transition-transform duration-300 ${hovered ? "translate-x-1" : ""}`} />
        </div>
      </div>

      {/* Tricolor stripe */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] flex overflow-hidden">
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, delay: hovered ? 0 : 0 }} className="flex-1 bg-[#002362] origin-left" />
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, delay: hovered ? 0.1 : 0 }} className="flex-1 bg-white origin-left" />
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, delay: hovered ? 0.2 : 0 }} className="flex-1 bg-[#ED2939] origin-left" />
      </div>
    </motion.button>
  );
};

export const Programs = () => {
  const [openSlug, setOpenSlug] = useState(null);
  const navigate = useNavigate();

  return (
    <section id="programs" data-testid="programs-section" className="section-padding bg-[#F7F6F2]">
      <div className="container-edge">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div className="max-w-2xl">
            <div className="overline mb-4">Nos formations</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight">
              Sept programmes,<br /> une même <em className="text-navy-500">ambition</em>.
            </h2>
          </div>
          <p className="text-[#555] max-w-md text-[15px] leading-relaxed">
            Du Bachelor à l'Executive MBA, choisissez le programme qui correspond à votre
            trajectoire. Chaque parcours est conçu comme un sprint d'innovation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {PROGRAMS.map((p, i) => (
            <Dialog
              key={p.slug}
              open={openSlug === p.slug}
              onOpenChange={(o) => setOpenSlug(o ? p.slug : null)}
            >
              <DialogTrigger asChild>
                <div>
                  <ProgramCard p={p} index={i} onOpen={() => setOpenSlug(p.slug)} />
                </div>
              </DialogTrigger>

              <DialogContent
                className="max-w-3xl max-h-[88vh] overflow-y-auto bg-white border border-navy-100 rounded-none"
                data-testid={`program-modal-${p.slug}`}
              >
                <DialogHeader>
                  <div className="overline mb-2" style={{ color: "#ED2939" }}>{p.level}</div>
                  <DialogTitle className="font-display text-3xl md:text-4xl text-navy-700 leading-tight">
                    {p.title}
                  </DialogTitle>
                  <DialogDescription className="text-[#444] text-[16px] leading-relaxed mt-4">
                    {p.short}
                  </DialogDescription>
                </DialogHeader>

                <div className="grid md:grid-cols-2 gap-8 mt-8">
                  <div>
                    <div className="overline mb-4">Modules clés</div>
                    <ul className="space-y-2.5">
                      {p.modules.map((m) => (
                        <li key={m} className="flex gap-3 text-[14px] text-[#333]">
                          <CheckCircle2 className="h-4 w-4 text-navy-500 shrink-0 mt-0.5" />
                          {m}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="overline mb-4">Débouchés</div>
                    <ul className="space-y-2.5">
                      {p.outcomes.map((o) => (
                        <li key={o} className="flex gap-3 text-[14px] text-[#333]">
                          <CheckCircle2 className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                          {o}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 p-5 bg-[#FAF8F3] border-l-4 border-navy-500">
                  <p className="text-sm text-navy-700 italic">{p.note}</p>
                </div>

                <div className="flex flex-wrap gap-3 mt-8">
                  <button
                    className="btn-red"
                    onClick={() => { setOpenSlug(null); navigate("/contact#candidater"); }}
                    data-testid={`program-apply-${p.slug}`}
                  >
                    Candidater
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    className="btn-outline-navy"
                    onClick={() => { setOpenSlug(null); navigate("/formations"); }}
                    data-testid={`program-formations-${p.slug}`}
                  >
                    Toutes les formations
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Programs;
